import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, FileText, MapPin, ArrowRight } from 'lucide-react';
import NetworkMenu from '../components/NetworkMenu.jsx';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans">
      <header className="border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-11 h-11 bg-[#032953] flex items-center justify-center p-1 rounded-sm">
              <img src="/atlas-symbol.png" alt="Atlas GEO Technologies" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-[#0a0a0a] font-bold text-base leading-none tracking-tight">ATLASPATH</span>
              <span className="text-[11px] text-[#DBC078] font-semibold uppercase tracking-[0.18em] mt-0.5">by Atlas GEO Technologies</span>
            </div>
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/dashboard" className="text-stone-700 hover:text-[#0a0a0a]">Console</Link>
            <Link to="/report"    className="text-stone-700 hover:text-[#0a0a0a]">Sample report</Link>
            <Link to="/pricing"   className="text-stone-700 hover:text-[#0a0a0a]">Pricing</Link>
            <NetworkMenu variant="dark" />
            <a href="mailto:info@atlasgeo.ai" className="bg-[#0a0a0a] text-white px-3 py-1.5 hover:bg-[#1a1a1a]">Contact</a>
          </nav>
        </div>
        <div className="gold-stripe" style={{ position: 'absolute', left: 0, right: 0, height: '3px' }} />
      </header>

      <section className="bg-gradient-to-br from-[#0a0a0a] to-[#2a2a2a] text-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-[#DBC078] mb-4">For VA municipalities &amp; public works directors</div>
            <h1 className="text-5xl md:text-6xl leading-tight font-serif-display mb-6">
              PROWAG compliance you can defend in court.
            </h1>
            <p className="text-lg text-stone-200 leading-relaxed mb-8">
              AtlasPath turns VDOT's 82,438-row public curb-ramp inventory into a county-specific ADA
              Transition Plan with calibrated, NIST-traceable measurements and dual-cited findings, ready to
              adopt under 28 CFR §35.150(d).
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard" className="bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a] font-medium px-5 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2">
                Open live demo <ArrowRight size={14} />
              </Link>
              <Link to="/report" className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-5 py-3 text-sm uppercase tracking-wider">
                View Fairfax sample
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <div>
                <div className="text-3xl text-[#DBC078] font-serif-display">82,438</div>
                <div className="text-xs uppercase tracking-wider text-stone-300 mt-1">VDOT ramps analyzed</div>
              </div>
              <div>
                <div className="text-3xl text-[#DBC078] font-serif-display">26,754</div>
                <div className="text-xs uppercase tracking-wider text-stone-300 mt-1">Noncompliant DWS</div>
              </div>
              <div>
                <div className="text-3xl text-[#DBC078] font-serif-display">±0.05pp</div>
                <div className="text-xs uppercase tracking-wider text-stone-300 mt-1">Slope tolerance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-[#1F7A8C] mb-2">Three reasons DPW directors sign</div>
            <h2 className="text-3xl text-[#0a0a0a] font-serif-display">A deliverable a city attorney will adopt</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Dual-cited findings', body: 'Every noncompliance references both the AtlasPath calibrated measurement AND the VDOT IIM-TE-376.1 derived grade. Cross-checked against the state\'s own published inventory.' },
              { icon: FileText, title: 'Government-procurement DOCX', body: 'Six artifacts per engagement: ADA Transition Plan, PROWAG Assessment, Asset Inventory, GeoJSON, Findings CSV, Calibration Appendix. Open formats, no vendor lock-in.' },
              { icon: CheckCircle2, title: 'NIST-traceable methodology', body: 'Stabila 196-2 smart level, Bosch GLM 40 laser, Mitutoyo digital caliper. Quarterly calibration validation. Mobile Device Pro LiDAR for 3D audit-trail preservation.' },
            ].map((b) => (
              <div key={b.title} className="bg-white border border-stone-200 p-6">
                <b.icon size={24} className="text-[#1F7A8C] mb-4" />
                <h3 className="text-lg text-[#0a0a0a] mb-2 font-serif-display">{b.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#1F7A8C] mb-2">The wedge</div>
              <h2 className="text-3xl text-[#0a0a0a] font-serif-display mb-4">VDOT's own data already documents the noncompliance.</h2>
              <p className="text-stone-700 leading-relaxed mb-6">
                26,754 statewide curb ramps have exposed-aggregate detectable warning surfaces, a pre-2010
                pattern that does not meet PROWAG R305.1.1. AtlasPath surfaces these findings from public
                inventory <em>before</em> taking a single calibrated measurement.
              </p>
              <p className="text-stone-700 leading-relaxed mb-6">
                For your county, the cost is real and the funding is available: SS4A, HSIP, CDBG, RAISE, and
                VDOT match under IIM-LD-55 all qualify.
              </p>
              <Link to="/dashboard" className="text-[#0a0a0a] font-medium hover:underline inline-flex items-center gap-2">
                See it in the live console <ArrowRight size={14} />
              </Link>
            </div>
            <div className="bg-[#0a0a0a] text-white p-8">
              <div className="text-xs uppercase tracking-widest text-[#DBC078] mb-3">Fairfax County · sample</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-3xl text-[#DBC078] font-serif-display">43,611</div>
                  <div className="text-xs text-stone-300">Ramps inventoried</div>
                </div>
                <div>
                  <div className="text-3xl text-[#DBC078] font-serif-display">27,074</div>
                  <div className="text-xs text-stone-300">Noncompliant (62.1%)</div>
                </div>
                <div>
                  <div className="text-3xl text-[#DBC078] font-serif-display">$37–50M</div>
                  <div className="text-xs text-stone-300">Remediation estimate</div>
                </div>
                <div>
                  <div className="text-3xl text-[#DBC078] font-serif-display">APCI 62</div>
                  <div className="text-xs text-stone-300">Fair · 0–100 scale</div>
                </div>
              </div>
              <Link to="/report" className="mt-6 bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a] font-medium px-4 py-2 text-sm uppercase tracking-wider inline-flex items-center gap-2">
                View the sample report <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0a] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-widest text-[#DBC078] mb-3">Pilot offer</div>
          <h2 className="text-4xl font-serif-display mb-4">One corridor. Six weeks. Fixed price.</h2>
          <p className="text-stone-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            500–800 curb ramps in a corridor of your choosing. Week 1 calibration validation. Weeks 2–4
            field capture. Week 5 analysis. Week 6 deliverable. If our deliverables don't enable your
            transition-plan team to identify and prioritize at least 100 noncompliance findings with
            defensible measurement basis, we refund 50%.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <a href="mailto:info@atlasgeo.ai?subject=AtlasPath%20pilot%20inquiry" className="bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a] font-medium px-6 py-3 text-sm uppercase tracking-wider">
              Request pilot scope
            </a>
            <Link to="/pricing" className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3 text-sm uppercase tracking-wider">
              See pricing tiers
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-stone-200 bg-stone-50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#1F7A8C] mb-2 text-center">AtlasGeo network</div>
          <h3 className="text-2xl text-[#0a0a0a] font-serif-display text-center mb-8">Other AtlasGeo products you can try right now</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'AtlasGeo home',         href: 'https://atlasgeo.ai',             tag: 'Main site' },
              { label: 'Atlas Corridor (maps)', href: 'https://maps.atlasgeo.ai',        tag: 'Interactive maps' },
              { label: 'Atlas Studio',          href: 'https://studio.atlasgeo.ai',      tag: 'Data → maps SaaS' },
              { label: 'AtlasSafe Explorer',    href: 'https://api.atlasgeo.ai/explore', tag: 'Crash insights' },
              { label: 'AtlasLive DMV',         href: 'https://live.atlasgeo.ai',        tag: 'Traffic + incidents' },
              { label: 'QMD Generator',         href: 'https://qmd.atlasgeo.ai',         tag: 'Map-prep demo' },
              { label: 'AtlasCare Access',      href: 'https://recursos.atlasgeo.ai',    tag: 'PW County resources' },
            ].map((p) => (
              <a key={p.href} href={p.href} target="_blank" rel="noreferrer noopener"
                 className="bg-white border border-stone-200 hover:border-[#1F7A8C] hover:shadow-soft p-3 transition-all group">
                <div className="text-sm text-[#0a0a0a] font-medium group-hover:text-[#1F7A8C]">{p.label} →</div>
                <div className="text-[11px] text-stone-500 mt-0.5">{p.tag}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-xs text-stone-500 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <MapPin size={12} />
            <span>AtlasGeo Technologies LLC · Commonwealth of Virginia · SDVOSB · MBE</span>
          </div>
          <div>
            <a href="mailto:info@atlasgeo.ai" className="hover:text-[#0a0a0a]">info@atlasgeo.ai</a>
            <span className="mx-2">·</span>
            <a href="https://atlasgeo.ai" className="hover:text-[#0a0a0a]">atlasgeo.ai</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
